/**
 * ASR Configuration Page
 * TASK-426: Per-tenant ASR provider toggle (FINMA local-Whisper tier).
 *
 * Lets an admin choose the speech-to-text backend for the account:
 *  - openai: audio is sent to OpenAI Whisper (cloud, default).
 *  - local:  audio is transcribed on-prem (whisper.cpp) and never leaves our infra.
 * Plus the model and language hint. Takes effect on the next transcription job.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Save, Mic, ShieldCheck } from 'lucide-react';
import { asrSettingsService } from '../../services/admin';
import { logger } from '../../utils/logger';
import {
  ASR_PROVIDER_OPTIONS,
  ASR_MODEL_OPTIONS,
  ASR_LANGUAGE_OPTIONS,
} from '../../types/admin';
import type { ASRProvider, ASRSettings, ASRSettingsUpdateData } from '../../types/admin';
import { Button } from '../../components/ui/button';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../utils/apiErrorHandler';

interface FormState {
  asr_provider: ASRProvider;
  asr_model: string;
  asr_language: string; // '' === auto-detect
}

const toFormState = (s: ASRSettings): FormState => ({
  asr_provider: s.asr_provider,
  asr_model: s.asr_model,
  asr_language: s.asr_language ?? '',
});

const ASRConfigPage: React.FC = () => {
  const [original, setOriginal] = useState<ASRSettings | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const settings = await asrSettingsService.getASRSettings();
      setOriginal(settings);
      setForm(toFormState(settings));
    } catch (error) {
      logger.error('Failed to load ASR settings:', error);
      toast.error(extractErrorMessage(error, 'Failed to load ASR settings'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (isLoading || !form) {
    return <div className="p-6 text-gray-500">Loading ASR configuration…</div>;
  }

  const hasChanges =
    !!original &&
    (form.asr_provider !== original.asr_provider ||
      form.asr_model !== original.asr_model ||
      form.asr_language !== (original.asr_language ?? ''));

  const providerChanged = !!original && form.asr_provider !== original.asr_provider;
  // Switching a tenant OFF on-prem back to the cloud is a compliance-sensitive
  // downgrade — call it out explicitly in the confirm dialog.
  const isComplianceDowngrade = providerChanged && original?.asr_provider === 'local';

  const handleProviderChange = (provider: ASRProvider) => {
    setForm((prev) => {
      if (!prev) return prev;
      const allowed = ASR_MODEL_OPTIONS[provider].map((m) => m.value);
      // Reset the model to the provider's default if the current one isn't valid.
      const model = allowed.includes(prev.asr_model) ? prev.asr_model : allowed[0];
      return { ...prev, asr_provider: provider, asr_model: model };
    });
  };

  const persist = async () => {
    if (!form) return;
    setIsSaving(true);
    try {
      const payload: ASRSettingsUpdateData = {
        asr_provider: form.asr_provider,
        asr_model: form.asr_model,
        asr_language: form.asr_language === '' ? null : form.asr_language,
      };
      const updated = await asrSettingsService.updateASRSettings(payload);
      setOriginal(updated);
      setForm(toFormState(updated));
      toast.success('ASR configuration saved. Takes effect on the next transcription job.');
    } catch (error) {
      logger.error('Failed to save ASR settings:', error);
      toast.error(extractErrorMessage(error, 'Failed to save ASR settings'));
    } finally {
      setIsSaving(false);
      setConfirmOpen(false);
    }
  };

  const handleSave = () => {
    // Confirm whenever the provider changes (the routing of regulated audio shifts).
    if (providerChanged) {
      setConfirmOpen(true);
      return;
    }
    persist();
  };

  return (
    <div className="p-6 max-w-2xl">
      <div className="flex items-center gap-2 mb-1">
        <Mic size={22} className="text-[#002B45]" />
        <h1 className="text-xl font-semibold text-gray-900">ASR / Transcription</h1>
      </div>
      <p className="text-gray-600 mb-6">
        Choose how meeting audio is transcribed for this tenant.
      </p>

      {/* Provider */}
      <section className="mb-6">
        <h2 className="text-sm font-semibold text-gray-700 mb-2">Provider</h2>
        <div className="space-y-2">
          {ASR_PROVIDER_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${
                form.asr_provider === opt.value
                  ? 'border-[#002B45] bg-[#002B45]/5'
                  : 'border-gray-200'
              }`}
            >
              <input
                type="radio"
                name="asr_provider"
                className="mt-1"
                checked={form.asr_provider === opt.value}
                onChange={() => handleProviderChange(opt.value)}
              />
              <span>
                <span className="block font-medium text-gray-900">{opt.label}</span>
                <span className="block text-sm text-gray-600">{opt.description}</span>
              </span>
            </label>
          ))}
        </div>
      </section>

      {/* Compliance banner for on-prem */}
      {form.asr_provider === 'local' && (
        <div className="mb-6 flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3">
          <ShieldCheck size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-800">
            Audio for this tenant is transcribed entirely on our own infrastructure and is
            <strong> never sent to OpenAI</strong> — suitable for FINMA-regulated clients.
          </p>
        </div>
      )}

      {/* Model */}
      <section className="mb-6">
        <h2 className="text-sm font-semibold text-gray-700 mb-2">Model</h2>
        <select
          className="w-full rounded-lg border border-gray-300 px-3 py-2"
          value={form.asr_model}
          onChange={(e) => setForm({ ...form, asr_model: e.target.value })}
        >
          {ASR_MODEL_OPTIONS[form.asr_provider].map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      </section>

      {/* Language */}
      <section className="mb-6">
        <h2 className="text-sm font-semibold text-gray-700 mb-2">Language</h2>
        <select
          className="w-full rounded-lg border border-gray-300 px-3 py-2"
          value={form.asr_language}
          onChange={(e) => setForm({ ...form, asr_language: e.target.value })}
        >
          {ASR_LANGUAGE_OPTIONS.map((l) => (
            <option key={l.value} value={l.value}>
              {l.label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500">
          For on-prem, <code>de-CH</code> selects the Swiss German fine-tune. Auto-detect
          works best for mixed-language meetings.
        </p>
      </section>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={!hasChanges || isSaving}>
          <Save size={16} className="mr-2" />
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Change ASR provider?"
        description={
          form.asr_provider === 'local'
            ? 'Audio for this tenant will be transcribed on-prem and will no longer be sent to OpenAI.'
            : 'Audio for this tenant will be sent to OpenAI (cloud) for transcription.'
        }
        warning={
          isComplianceDowngrade
            ? 'This tenant is currently on-prem (FINMA). Switching to OpenAI means audio will be sent to a cloud sub-processor.'
            : undefined
        }
        onConfirm={persist}
        confirmText="Save"
        destructive={isComplianceDowngrade}
        isLoading={isSaving}
      />
    </div>
  );
};

export default ASRConfigPage;
